import {inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {SignupSourceService} from './signup-source.service';
import {GclidService} from './gclid.service';

const PROP_MAX_LEN = 100;

/**
 * Thin wrapper over the self-hosted Umami tracker (window.umami), loaded via
 * the <script> tag in index.html against the first-party /umami/* proxy (see
 * src/_redirects) rather than umami.notionhabitheroes.com directly, so ad/
 * tracker blockers targeting third-party analytics hosts don't strip it.
 *
 * Umami's `umami.track(name, data)` takes a flat data object — unlike
 * Plausible's `plausible(name, {props})`, there is no nesting. SSR-safe via
 * isPlatformBrowser; no-ops if the global is absent (e.g. before the script
 * has loaded, or if it's blocked) so every trackEvent() call site stays valid
 * regardless of tracker availability.
 */
@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {

  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly signupSourceService = inject(SignupSourceService);
  private readonly gclidService = inject(GclidService);

  /**
   * Props for the Umami `Signup` event: the signup method plus the stored
   * first-touch source (utm_source / utm_medium / utm_campaign, referrer
   * hostname only, `gclid: true` flag without the value). Empty values are
   * omitted, strings are cut to 100 chars; no user identifier is included.
   *
   * Call this BEFORE sending the signup request: AuthenticationService clears
   * the stored source/gclid in its `tap` on success, which runs before the
   * component's subscribe callback that fires the event.
   */
  buildSignupProps(method: string): Record<string, string | boolean> {
    const props: Record<string, string | boolean> = {method};
    const source = this.signupSourceService.getValidSource();
    const put = (key: string, value: string | undefined): void => {
      if (value) {
        props[key] = value.slice(0, PROP_MAX_LEN);
      }
    };
    put('utm_source', source?.utmSource);
    put('utm_medium', source?.utmMedium);
    put('utm_campaign', source?.utmCampaign);
    if (source?.referrer) {
      try {
        put('referrer_host', new URL(source.referrer).hostname);
      } catch {
        // unparsable referrer: drop it, never send the raw string
      }
    }
    if (this.gclidService.getValidGclid()) {
      props['gclid'] = true;
    }
    return props;
  }

  trackEvent(name: string, props?: Record<string, string | number | boolean>): void {
    if (!this.isBrowser) {
      return;
    }
    const umami = (window as any).umami;
    if (!umami) {
      return;
    }
    if (props) {
      umami.track(name, props);
    } else {
      umami.track(name);
    }
  }
}
